import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useTranslation } from "react-i18next";
import {
  meetingMediaUrl,
  meetingPhotoDisplayUrl,
} from "../../api/Meetings/meetingSessionsApi";
import {
  isMeetingPdfPhoto,
  mediaKindFromMime,
} from "../../utils/meetingRecorder";
import {
  meetingLinkedSampleHintName,
  meetingLinkedSampleNames,
  meetingPhotoLinkedSample,
} from "./meetingLinkedSampleLabel";
import {
  cachedMeetingPhotoFile,
  copyMeetingPhotoFileToClipboard,
  fetchMeetingPhotoAsFile,
  startMeetingPhotoDrag,
} from "./meetingPhotoDrag";
import MeetingMediaTeamNoteEditor from "./MeetingMediaTeamNoteEditor";

/** Visor: media de la reunión (no el front de muestra, que se repite entre fotos). */
function previewMediaSrc(photo) {
  if (!photo) return "";
  const raw =
    photo.fileUrl || photo.displayFileUrl || photo.muestraFrontUrl || "";
  return meetingMediaUrl(raw) || meetingPhotoDisplayUrl(photo);
}

/** Paleta estable por índice de grupo (contraste bueno en light/dark). */
const GROUP_CHIP_TONES = [
  { main: "#0f766e", soft: "#ccfbf1" },
  { main: "#9a3412", soft: "#ffedd5" },
  { main: "#1d4ed8", soft: "#dbeafe" },
  { main: "#7c3aed", soft: "#ede9fe" },
  { main: "#b45309", soft: "#fef3c7" },
  { main: "#be123c", soft: "#ffe4e6" },
  { main: "#0e7490", soft: "#cffafe" },
  { main: "#4d7c0f", soft: "#ecfccb" },
];

function groupToneForIndex(index) {
  const i = Number(index);
  if (!Number.isFinite(i) || i < 0) {
    return { main: "#64748b", soft: "#f1f5f9" };
  }
  return GROUP_CHIP_TONES[i % GROUP_CHIP_TONES.length];
}

function PreviewGroupChip({ groupMeta, t }) {
  if (!groupMeta) return null;
  const ungrouped = Boolean(groupMeta.ungrouped);
  const tone = ungrouped
    ? { main: "#64748b", soft: "#f1f5f9" }
    : groupToneForIndex(groupMeta.index);
  const label = ungrouped
    ? t("Meetings.mediaUngrouped")
    : String(groupMeta.label || "").trim() ||
      t("Meetings.mediaPreviewGroupFallback", {
        n: (groupMeta.index ?? 0) + 1,
      });

  return (
    <Chip
      size="small"
      icon={
        ungrouped ? (
          <InboxOutlinedIcon sx={{ fontSize: "15px !important" }} />
        ) : (
          <FolderOutlinedIcon sx={{ fontSize: "15px !important" }} />
        )
      }
      label={label}
      sx={{
        height: 26,
        maxWidth: "100%",
        fontWeight: 800,
        letterSpacing: 0.2,
        borderRadius: 1.5,
        color: tone.main,
        bgcolor: (th) =>
          th.palette.mode === "dark" ? alpha(tone.main, 0.22) : tone.soft,
        border: "1px solid",
        borderColor: alpha(tone.main, 0.35),
        boxShadow: `inset 3px 0 0 ${tone.main}`,
        "& .MuiChip-icon": {
          color: tone.main,
          ml: 0.75,
        },
        "& .MuiChip-label": {
          px: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  );
}

function sectionLabelSx(theme) {
  return {
    fontWeight: 700,
    color: "text.secondary",
    letterSpacing: 0.2,
    textTransform: "uppercase",
    fontSize: "0.65rem",
    mb: 0.35,
  };
}

function NoteBlock({ label, text, theme }) {
  if (!text) return null;
  return (
    <Box>
      <Typography variant="caption" sx={sectionLabelSx(theme)}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          lineHeight: 1.45,
          overflowWrap: "anywhere",
          fontSize: "0.8125rem",
          color: "text.secondary",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

/**
 * Visor de foto/vídeo de reunión con panel de detalles + nota STAGE.
 * No sustituye Detalle/Media: sirve para probar el flujo «solo Galería».
 */
export default function MeetingMediaPreviewDialog({
  open,
  photo,
  onClose,
  isNarrow = false,
  notesById,
  audioByPhotoId,
  summaryLang = "es",
  previewDragFile = null,
  onSaveTeamNote,
  onOpenLinkedSample,
  onPrev = null,
  onNext = null,
  canPrev = false,
  canNext = false,
  navIndex = -1,
  navTotal = 0,
  groupMeta = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [copyHint, setCopyHint] = useState(false);
  const [copyOk, setCopyOk] = useState(true);
  const [copyBusy, setCopyBusy] = useState(false);

  const isVideo = Boolean(
    photo &&
    (photo.kind === "video" ||
      mediaKindFromMime(photo.mimeType) === "video" ||
      String(photo.mimeType || "")
        .toLowerCase()
        .startsWith("video/")),
  );
  const isPdf = Boolean(photo && isMeetingPdfPhoto(photo));

  const copyForIllustrator = useCallback(async () => {
    if (!photo || isVideo || isPdf || copyBusy) return;
    setCopyBusy(true);
    try {
      let file = previewDragFile || cachedMeetingPhotoFile(photo) || null;
      if (!file) file = await fetchMeetingPhotoAsFile(photo);
      const ok = file ? await copyMeetingPhotoFileToClipboard(file) : false;
      setCopyOk(ok);
      setCopyHint(true);
    } finally {
      setCopyBusy(false);
    }
  }, [photo, isVideo, isPdf, copyBusy, previewDragFile]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        String(e.key).toLowerCase() === "c" &&
        !isVideo &&
        !isPdf
      ) {
        e.preventDefault();
        copyForIllustrator();
        return;
      }
      if (e.key === "ArrowLeft" && canPrev) {
        e.preventDefault();
        onPrev?.();
      } else if (e.key === "ArrowRight" && canNext) {
        e.preventDefault();
        onNext?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    open,
    canPrev,
    canNext,
    onPrev,
    onNext,
    isVideo,
    isPdf,
    copyForIllustrator,
  ]);

  const sampleLink = meetingPhotoLinkedSample(photo);
  const sampleNames = meetingLinkedSampleNames(sampleLink || {});

  const noteBundle = useMemo(() => {
    if (!photo) {
      return {
        spoken: [],
        visual: [],
        linked: [],
        sourceText: "",
        sourceLabelKey: null,
        audioSeq: null,
      };
    }
    const bucket = notesById?.get?.(Number(photo.id));
    const audioLink = audioByPhotoId?.get?.(Number(photo.id));
    const preferTranslatedVisual = summaryLang === "en";
    const visual =
      isVideo || preferTranslatedVisual ? bucket?.visual || [] : [];
    const spoken = [
      ...(bucket?.spoken || []),
      ...(!isVideo && !preferTranslatedVisual ? audioLink?.spoken || [] : []),
    ];
    const linked = bucket?.linked || [];
    const sourceText = String(
      isVideo ? photo.transcriptText : photo.visionText || "",
    ).trim();
    const audioSeq =
      audioLink?.chunk?.seq != null ? Number(audioLink.chunk.seq) + 1 : null;
    return {
      spoken,
      visual,
      linked,
      sourceText,
      sourceLabelKey: isVideo
        ? "Meetings.mediaTranscript"
        : "Meetings.mediaVisionRead",
      audioSeq,
    };
  }, [photo, notesById, audioByPhotoId, summaryLang, isVideo]);

  const linkedSamples = useMemo(() => {
    if (!photo) return [];
    if (Array.isArray(photo.linkedMuestras) && photo.linkedMuestras.length) {
      return photo.linkedMuestras;
    }
    if (photo.idMuestra && photo.muestraNombre) {
      return [
        {
          idMuestra: photo.idMuestra,
          muestraNombre: photo.muestraNombre,
        },
      ];
    }
    return [];
  }, [photo]);

  /** Más alto para prendas de pie; contain evita recortes. */
  const mediaStageH = isNarrow
    ? "min(72dvh, calc(100dvh - 168px))"
    : "min(78vh, calc(100dvh - 120px))";
  const [mediaReady, setMediaReady] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const mediaSrc = previewMediaSrc(photo);
  // X-Frame-Options SAMEORIGIN bloquea iframe cross-origin; blob: es same-origin.
  const pdfFrameSrc = pdfBlobUrl || "";

  useEffect(() => {
    setMediaReady(false);
  }, [photo?.id, mediaSrc]);

  useEffect(() => {
    if (!open || !isPdf || !mediaSrc) {
      setPdfBlobUrl("");
      setPdfLoadError(false);
      return undefined;
    }
    if (/^(blob:|data:)/i.test(mediaSrc)) {
      setPdfBlobUrl(mediaSrc);
      setPdfLoadError(false);
      setMediaReady(true);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = "";
    setPdfBlobUrl("");
    setPdfLoadError(false);
    setMediaReady(false);
    (async () => {
      try {
        const res = await fetch(mediaSrc, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const typed =
          blob.type && blob.type !== "application/octet-stream"
            ? blob
            : new Blob([blob], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(typed);
        setPdfBlobUrl(objectUrl);
        setMediaReady(true);
      } catch {
        if (!cancelled) {
          setPdfBlobUrl("");
          setPdfLoadError(true);
          setMediaReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, isPdf, mediaSrc, photo?.id]);

  const mediaKindLabel = isPdf
    ? "PDF"
    : isVideo
      ? t("Meetings.video")
      : t("Meetings.photo");

  return (
    <>
      <Dialog
        open={open && Boolean(photo)}
        onClose={onClose}
        fullWidth
        fullScreen={isNarrow}
        maxWidth="lg"
        className="meeting-no-print"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.5)",
            },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.5)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mb: 0.5 }}
            >
              <PreviewGroupChip groupMeta={groupMeta} t={t} />
              {navTotal > 1 && navIndex >= 0 ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("Meetings.mediaPreviewNav", {
                    current: navIndex + 1,
                    total: navTotal,
                  })}
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    borderRadius: 1.25,
                    bgcolor: (th) => alpha(th.palette.text.primary, 0.04),
                    "& .MuiChip-label": { px: 0.9 },
                  }}
                />
              ) : null}
            </Stack>
            {sampleNames.cliente ? (
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                {sampleNames.cliente}
              </Typography>
            ) : null}
            <Typography
              variant={sampleNames.cliente ? "body2" : "subtitle1"}
              component="div"
              color={sampleNames.cliente ? "text.secondary" : "inherit"}
              sx={{
                fontWeight: sampleNames.cliente ? 600 : 800,
                lineHeight: 1.35,
              }}
            >
              {[
                sampleNames.samp && sampleNames.samp !== sampleNames.cliente
                  ? sampleNames.samp
                  : null,
                `${mediaKindLabel} #${photo?.id || ""}`,
                photo?.offsetLabel || null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {isPdf && mediaSrc ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                component="a"
                href={mediaSrc}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewOutlinedIcon />}
                sx={{ fontWeight: 700, textTransform: "none", mr: 0.5 }}
              >
                PDF
              </Button>
            ) : null}
            {!isVideo && !isPdf ? (
              <Tooltip title={t("Meetings.mediaCopyForIllustrator")}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={copyBusy}
                  startIcon={
                    copyBusy ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <ContentCopyOutlinedIcon />
                    )
                  }
                  onClick={copyForIllustrator}
                  sx={{ fontWeight: 700, textTransform: "none", mr: 0.5 }}
                >
                  {t("Meetings.mediaCopyForIllustrator")}
                </Button>
              </Tooltip>
            ) : null}
            <IconButton aria-label={t("Meetings.cancel")} onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 0,
            pb: { xs: 2, md: 2.5 },
            overflow: "auto",
          }}
        >
          {photo ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1.45fr) minmax(240px, 0.75fr)",
                },
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  position: "relative",
                  height: mediaStageH,
                  minHeight: mediaStageH,
                  borderRadius: 1.5,
                  bgcolor: isVideo
                    ? "common.black"
                    : isPdf
                      ? "background.paper"
                      : "action.hover",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!mediaReady ? (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "grid",
                      placeItems: "center",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  >
                    <CircularProgress size={28} thickness={4} />
                  </Box>
                ) : null}
                <Box
                  key={`preview-media-${photo.id}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: mediaReady ? 1 : 0.35,
                    transition: "opacity 120ms ease",
                  }}
                >
                  {isPdf ? (
                    pdfFrameSrc ? (
                      <Box
                        component="iframe"
                        title={
                          photo.originalFileName ||
                          photo.caption ||
                          `PDF #${photo.id}`
                        }
                        src={pdfFrameSrc}
                        onLoad={() => setMediaReady(true)}
                        onError={() => setMediaReady(true)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          border: 0,
                          bgcolor: "background.paper",
                        }}
                      />
                    ) : (
                      <Stack
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="center"
                        sx={{ px: 2, textAlign: "center" }}
                      >
                        {pdfLoadError ? (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {t("Meetings.mediaPdfPreviewFailed") ||
                                "No se pudo embeber el PDF aquí."}
                            </Typography>
                            {mediaSrc ? (
                              <Button
                                size="small"
                                variant="contained"
                                component="a"
                                href={mediaSrc}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<OpenInNewOutlinedIcon />}
                                sx={{ fontWeight: 700, textTransform: "none" }}
                              >
                                PDF
                              </Button>
                            ) : null}
                          </>
                        ) : (
                          <CircularProgress size={28} thickness={4} />
                        )}
                      </Stack>
                    )
                  ) : isVideo ? (
                    <Box
                      component="video"
                      src={mediaSrc}
                      controls
                      autoPlay
                      playsInline
                      onLoadedData={() => setMediaReady(true)}
                      onError={() => setMediaReady(true)}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        bgcolor: "black",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      draggable
                      onDragStart={(e) => {
                        const file =
                          previewDragFile || cachedMeetingPhotoFile(photo);
                        startMeetingPhotoDrag(e, photo, file);
                        if (file) {
                          copyMeetingPhotoFileToClipboard(file).catch(() => {});
                        }
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        cursor: "grab",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={mediaSrc}
                        alt={
                          sampleNames.cliente ||
                          photo.caption ||
                          photo.teamNote ||
                          ""
                        }
                        draggable={false}
                        onLoad={() => setMediaReady(true)}
                        onError={(e) => {
                          const fallback = photo.muestraFrontUrl
                            ? meetingMediaUrl(photo.muestraFrontUrl)
                            : meetingPhotoDisplayUrl(photo);
                          if (fallback && e?.currentTarget?.src !== fallback) {
                            e.currentTarget.src = fallback;
                            return;
                          }
                          setMediaReady(true);
                        }}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                          objectPosition: "center",
                          display: "block",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                  )}
                </Box>
                {canPrev || canNext ? (
                  <>
                    <IconButton
                      aria-label={t("Meetings.mediaPreviewPrev")}
                      onClick={() => onPrev?.()}
                      disabled={!canPrev}
                      sx={{
                        position: "absolute",
                        left: 6,
                        top: "50%",
                        transform: "translateY(-50%)",
                        bgcolor: alpha(theme.palette.common.black, 0.45),
                        color: "common.white",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.common.black, 0.65),
                        },
                        "&.Mui-disabled": {
                          bgcolor: alpha(theme.palette.common.black, 0.2),
                          color: alpha(theme.palette.common.white, 0.35),
                        },
                      }}
                    >
                      <ChevronLeftOutlinedIcon />
                    </IconButton>
                    <IconButton
                      aria-label={t("Meetings.mediaPreviewNext")}
                      onClick={() => onNext?.()}
                      disabled={!canNext}
                      sx={{
                        position: "absolute",
                        right: 6,
                        top: "50%",
                        transform: "translateY(-50%)",
                        bgcolor: alpha(theme.palette.common.black, 0.45),
                        color: "common.white",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.common.black, 0.65),
                        },
                        "&.Mui-disabled": {
                          bgcolor: alpha(theme.palette.common.black, 0.2),
                          color: alpha(theme.palette.common.white, 0.35),
                        },
                      }}
                    >
                      <ChevronRightOutlinedIcon />
                    </IconButton>
                  </>
                ) : null}
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  maxHeight: { xs: "none", md: "70vh" },
                  overflow: "auto",
                  pr: { md: 0.5 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                {linkedSamples.length ? (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={0.5}
                  >
                    {linkedSamples.map((link) => {
                      const names = meetingLinkedSampleNames(link);
                      const chips = [];
                      if (names.cliente) {
                        chips.push(
                          <Chip
                            key={`${link.idMuestra}-cliente`}
                            size="small"
                            color="primary"
                            label={names.cliente}
                            onClick={() => onOpenLinkedSample?.(link.idMuestra)}
                            sx={{ height: 22, fontWeight: 800 }}
                            title={t("Meetings.linkedSampleHint", {
                              name: meetingLinkedSampleHintName(link),
                            })}
                          />,
                        );
                      }
                      if (names.samp && names.samp !== names.cliente) {
                        chips.push(
                          <Chip
                            key={`${link.idMuestra}-samp`}
                            size="small"
                            color={names.cliente ? "default" : "primary"}
                            variant={names.cliente ? "outlined" : "filled"}
                            label={names.samp}
                            onClick={() => onOpenLinkedSample?.(link.idMuestra)}
                            sx={{ height: 22, fontWeight: 800 }}
                            title={t("Meetings.linkedSampleHint", {
                              name: meetingLinkedSampleHintName(link),
                            })}
                          />,
                        );
                      }
                      if (!chips.length && names.fallback) {
                        chips.push(
                          <Chip
                            key={`${link.idMuestra}-id`}
                            size="small"
                            color="primary"
                            label={names.fallback}
                            onClick={() => onOpenLinkedSample?.(link.idMuestra)}
                            sx={{ height: 22, fontWeight: 800 }}
                          />,
                        );
                      }
                      return chips;
                    })}
                    {/se queda/i.test(
                      String(photo.caption || photo.teamNote || ""),
                    ) ? (
                      <Chip
                        size="small"
                        color="info"
                        label={t("Meetings.leaveSamplesBadge")}
                        sx={{ height: 22, fontWeight: 800 }}
                      />
                    ) : null}
                  </Stack>
                ) : null}

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 800, color: "text.secondary" }}
                  >
                    {mediaKindLabel}
                  </Typography>
                  {photo.offsetLabel ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                    >
                      {photo.offsetLabel}
                    </Typography>
                  ) : null}
                  {String(photo.uploadedByUsername || "").trim() ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("Meetings.mediaUploadedBy", {
                        name: String(photo.uploadedByUsername).trim(),
                      })}
                      sx={{ height: 20, fontWeight: 700 }}
                    />
                  ) : null}
                  {!isVideo && !isPdf && noteBundle.audioSeq != null ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={t("Meetings.photoLinkedAudio", {
                        n: noteBundle.audioSeq,
                      })}
                      sx={{ height: 20, fontWeight: 700 }}
                    />
                  ) : null}
                </Stack>

                <MeetingMediaTeamNoteEditor
                  photo={photo}
                  onSaveTeamNote={onSaveTeamNote}
                  t={t}
                  sx={{ mt: 0 }}
                />

                {noteBundle.spoken.length ? (
                  <NoteBlock
                    theme={theme}
                    label={t("Meetings.mediaNoteSpoken")}
                    text={noteBundle.spoken.join("\n")}
                  />
                ) : null}
                {noteBundle.visual.length ? (
                  <NoteBlock
                    theme={theme}
                    label={t("Meetings.mediaNoteVisual")}
                    text={noteBundle.visual.join("\n")}
                  />
                ) : null}
                {noteBundle.linked.length ? (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.45,
                      overflowWrap: "anywhere",
                      fontSize: "0.8125rem",
                      color: "text.secondary",
                    }}
                  >
                    {noteBundle.linked.join("\n")}
                  </Typography>
                ) : null}

                {noteBundle.sourceText && noteBundle.sourceLabelKey ? (
                  <Box>
                    <Typography variant="caption" sx={sectionLabelSx(theme)}>
                      {t(noteBundle.sourceLabelKey)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.45,
                        overflowWrap: "anywhere",
                        fontSize: "0.8125rem",
                        color: "text.secondary",
                        maxHeight: 180,
                        overflow: "auto",
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                      }}
                    >
                      {noteBundle.sourceText}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={copyHint}
        autoHideDuration={5000}
        onClose={() => setCopyHint(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={copyOk ? "success" : "warning"}
          variant="filled"
          onClose={() => setCopyHint(false)}
        >
          {copyOk
            ? t("Meetings.mediaCopiedForIllustrator")
            : t("Meetings.mediaCopyFailed")}
        </Alert>
      </Snackbar>
    </>
  );
}
